import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatPageTitle,
  getMetaSpec,
  nsFileToI18n,
  resolvePageNameFromPath,
  toI18nKey,
} from '../../lib/seoRouteMeta';

interface DynamicOpenGraphProps {
  page?: string;
  customTitle?: string;
  customDescription?: string;
  customImage?: string;
}

function readT(
  t: (key: string, opts?: Record<string, unknown>) => string,
  ns: string,
  keyPath: string | null | undefined,
): string | null {
  if (!keyPath) return null;
  const key = toI18nKey(keyPath);
  if (!key) return null;
  const val = t(key, { ns, defaultValue: '' });
  if (!val || val === key || val.length < 2) return null;
  return val;
}

export const DynamicOpenGraph: React.FC<DynamicOpenGraphProps> = ({
  page = 'home',
  customTitle,
  customDescription,
  customImage = '/1200x630.png',
}) => {
  const { t, i18n } = useTranslation([
    'seo',
    'compare',
    'pricing',
    'resto',
    'office',
    'home',
    'music',
    'team',
    'contact',
    'about',
    'blog',
    'press',
    'careers',
    'support',
    'legal',
  ]);

  useEffect(() => {
    const updateMetaTags = () => {
      const pathname = window.location.pathname;
      const pageName = resolvePageNameFromPath(pathname);
      const spec = getMetaSpec(pageName);

      let title = customTitle || '';
      let description = customDescription || '';
      let keywords = '';

      if (!customTitle || !customDescription) {
        if (pageName.startsWith('compare/') && pageName !== 'compare') {
          // Landings keep their own meta via SSR; runtime leaves them unless missing
          const slug = pageName.replace('compare/', '');
          const ns = /^(resto|office|entertainment)-vs-/.test(slug) ? 'sector-vs' : 'compare-vs';
          title = title || t(`pages.${slug}.meta_title`, { ns, defaultValue: '' }) || document.title;
          description =
            description ||
            t(`pages.${slug}.meta_description`, { ns, defaultValue: '' }) ||
            '';
        } else if (spec?.skinNs) {
          const skinNs = nsFileToI18n(spec.skinNs) || 'seo';
          const pricingNs = 'pricing';
          const skinTitle =
            readT(t, skinNs, spec.skinTitleKey) ||
            readT(t, skinNs, spec.fallbackSkinTitleKey);
          const pricingTitle = readT(t, pricingNs, 'title');
          title =
            title ||
            (skinTitle && pricingTitle ? `${pricingTitle} — ${skinTitle}` : null) ||
            skinTitle ||
            spec.title;
          description =
            description ||
            readT(t, skinNs, spec.skinDescKey) ||
            readT(t, skinNs, spec.fallbackSkinDescKey) ||
            readT(t, pricingNs, 'seo.pricingDescription') ||
            readT(t, pricingNs, 'subtitle') ||
            spec.desc;
        } else if (spec) {
          const ns = nsFileToI18n(spec.ns) || 'seo';
          title =
            title ||
            readT(t, ns, spec.titleKey) ||
            readT(t, ns, spec.fallbackTitleKey) ||
            spec.title;
          description =
            description ||
            readT(t, ns, spec.descKey) ||
            readT(t, ns, spec.fallbackDescKey) ||
            spec.desc;
          keywords = readT(t, ns, 'seo.keywords') || readT(t, 'seo', 'seo.keywords') || '';
        } else if (page === 'compare') {
          title = title || t('seo.title', { ns: 'compare', defaultValue: '' }) || spec?.title || '';
          description =
            description || t('seo.description', { ns: 'compare', defaultValue: '' }) || '';
        } else {
          title = title || t('seo.title', { ns: 'seo', defaultValue: '' }) || t('title', { ns: 'seo', defaultValue: 'Komby' });
          description =
            description ||
            t('seo.description', { ns: 'seo', defaultValue: '' }) ||
            t('description', { ns: 'seo', defaultValue: '' });
          keywords = t('seo.keywords', { ns: 'seo', defaultValue: '' }) || t('keywords', { ns: 'seo', defaultValue: '' });
        }
      }

      if (!keywords) {
        keywords = t('seo.keywords', { ns: 'seo', defaultValue: '' }) || '';
      }

      const formattedTitle = formatPageTitle(title);
      const appName = t('seo.appName', { ns: 'seo', defaultValue: '' }) || t('appName', { ns: 'seo', defaultValue: 'Komby' });

      const currentUrl = window.location.href;
      const ogImage = customImage.startsWith('http')
        ? customImage
        : `${window.location.origin}${customImage}`;

      const ogLocale = i18n.language.includes('-')
        ? i18n.language.replace('-', '_')
        : `${i18n.language}_${i18n.language.toUpperCase()}`;

      const updateMetaTag = (property: string, content: string, isProperty = true) => {
        if (!content) return;
        const selector = isProperty ? `meta[property="${property}"]` : `meta[name="${property}"]`;
        let meta = document.querySelector(selector) as HTMLMetaElement | null;

        if (!meta) {
          meta = document.createElement('meta');
          if (isProperty) {
            meta.setAttribute('property', property);
          } else {
            meta.setAttribute('name', property);
          }
          meta.setAttribute('data-dynamic-og', 'true');
          document.head.appendChild(meta);
        }

        meta.setAttribute('content', content);
      };

      document.title = formattedTitle;
      updateMetaTag('description', description, false);
      if (keywords) updateMetaTag('keywords', keywords, false);

      updateMetaTag('og:type', 'website');
      updateMetaTag('og:url', currentUrl);
      updateMetaTag('og:title', formattedTitle);
      updateMetaTag('og:description', description);
      updateMetaTag('og:image', ogImage);
      updateMetaTag('og:locale', ogLocale);
      updateMetaTag('og:site_name', appName);

      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:url', currentUrl);
      updateMetaTag('twitter:title', formattedTitle);
      updateMetaTag('twitter:description', description);
      updateMetaTag('twitter:image', ogImage);

      updateMetaTag('og:image:width', '1200');
      updateMetaTag('og:image:height', '630');
      updateMetaTag('og:image:type', 'image/png');
    };

    const timer = setTimeout(updateMetaTags, 100);
    return () => clearTimeout(timer);
  }, [page, i18n.language, customTitle, customDescription, customImage, t]);

  return null;
};
